import { ChatBubbleOutlined } from '@mui/icons-material';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import type { MouseEvent } from 'react';

import useTranslate from 'hooks/useTranslate';

interface ChatHeaderProps {
    users: string[];
    isBackOffice: boolean;
    canAccessBackoffice: boolean;
    hasBackofficeAssigned: boolean;
    onToggle: () => void;
}

const ChatHeader = ({
    users,
    isBackOffice,
    canAccessBackoffice,
    hasBackofficeAssigned,
    onToggle,
}: ChatHeaderProps) => {
    const { translate } = useTranslate();
    const participants = users.filter(Boolean).join(', ');
    const backofficeDisabledReason = !hasBackofficeAssigned
        ? translate('backOfficeChatUnavailableNoAgent')
        : !canAccessBackoffice
          ? translate('backOfficeChatUnavailableNoAccess')
          : '';
    const isBackofficeDisabled = Boolean(backofficeDisabledReason);

    const handleToggle = (_: MouseEvent<HTMLElement>, value: 'client' | 'backoffice' | null) => {
        if (!value) {
            return;
        }

        const nextIsBackOffice = value === 'backoffice';
        if (nextIsBackOffice === isBackOffice) {
            return;
        }

        if (nextIsBackOffice && isBackofficeDisabled) {
            return;
        }

        onToggle();
    };

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 2 }}
            sx={{
                px: { xs: 1.5, sm: 2.5 },
                py: { xs: 1.5, sm: 2 },
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
                gap: { xs: 1.25, sm: 2 },
            }}
        >
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
                <Box
                    sx={{
                        width: { xs: 40, sm: 44 },
                        height: { xs: 40, sm: 44 },
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'primary.main',
                        bgcolor: 'action.selected',
                    }}
                >
                    <ChatBubbleOutlined sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </Box>

                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.3,
                        }}
                    >
                        {translate('chat')}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        title={participants || undefined}
                        sx={{ fontWeight: 500 }}
                    >
                        {participants
                            ? `${translate('inConversation')} ${participants}`
                            : translate('clientChatDescription')}
                    </Typography>
                </Stack>
            </Stack>

            <ToggleButtonGroup
                exclusive
                size="small"
                value={isBackOffice ? 'backoffice' : 'client'}
                onChange={handleToggle}
                sx={{
                    alignSelf: { xs: 'stretch', sm: 'center' },
                    flexShrink: 0,
                    '& .MuiToggleButton-root': {
                        flex: { xs: 1, sm: 'initial' },
                        px: { xs: 1.25, sm: 1.75 },
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8125rem',
                        whiteSpace: 'nowrap',
                    },
                }}
            >
                <ToggleButton value="client">{translate('client')}</ToggleButton>
                <Tooltip
                    title={backofficeDisabledReason}
                    disableHoverListener={!isBackofficeDisabled}
                    disableFocusListener={!isBackofficeDisabled}
                >
                    <span>
                        <ToggleButton value="backoffice" disabled={isBackofficeDisabled}>
                            {translate('backOfficeChat')}
                        </ToggleButton>
                    </span>
                </Tooltip>
            </ToggleButtonGroup>
        </Stack>
    );
};

export default ChatHeader;
